import * as babylon from "@babylonjs/core";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import { BabylonState, Entity } from "../../state";

/**
 * Solves the 'distant origin' problem by centering the scene around
 * the point the camera is looking at.
 *
 * Distant origin arises from the float64 JS numbers being converted to
 * float32 precision in the gpu shaders.
 *
 * 64-bit floats can accurately represent about 1e15 decimal places,
 * allowing them to model 1e9km down to millimeter precision.
 * (This is ~6.7AU, eg the solar system out to Jupiter)
 *
 * 32-bit floats can only represent about 1e6-1e7 decimal places,
 * so they can only model a region less than 10km across to millimeter precision.
 *
 * For an example of what it looks like when looking at objects
 * far from the origin, see here (photosensitivity warning!):
 * https://www.reddit.com/r/Unity3D/comments/148ctmb/dont_forget_floating_origin_if_youre_working_with/
 */
export function applyRenderOrigin(state: BabylonState) {
  const origin = state.sceneConfig.camera?.focusPoint ?? babylon.Vector3.Zero();

  // Position all meshes based on the render origin.
  //
  // Every anchor is read off `globalPosition`, never `position`: the latter is
  // parent-relative for a child. Gating on `globalPosition` rather than
  // `position` matters too — a mesh-bearing child that takes its whole pose from
  // its parent has no `Position` at all, and skipping it would leave it pinned at
  // render-space zero, appearing to slide across the world as the camera pans.
  for (const entity of Object.values(state.entities)) {
    const global = entity.globalPosition;
    if (!global) {
      continue;
    }
    // `mesh` is absent while a `model_node`'s geometry is still loading.
    if (entity.mesh?.mesh) {
      entity.mesh.mesh.position = global.current.subtract(origin);
      // The composed orientation, which the mesh no longer carries itself:
      // `cloneNode` bakes only scale, precisely so a joint above this entity can
      // turn it. Unaffected by the origin shift, which is a translation.
      entity.mesh.mesh.rotationQuaternion = global.rotation;
    }
    // A model is placed by its anchor, never by its own roots: for a glTF those
    // are Babylon's `__root__`, whose rotation and scaling *are* the
    // glTF-to-Babylon handedness conversion. Writing this orientation onto one
    // would replace that conversion rather than compose with it, and lay the
    // model on its side. Everything the model's own joints write sits below the
    // anchor and is untouched by translating it.
    if (entity.mesh?.model) {
      entity.mesh.model.anchor.position = global.current.subtract(origin);
      entity.mesh.model.anchor.rotationQuaternion = global.rotation;
    }
    if (entity.path?.mesh) {
      entity.path.mesh.position = global.mean.subtract(origin);
    }
    if (entity.vectorField?.mesh) {
      entity.vectorField.mesh.position = global.mean.subtract(origin);
    }
    if (entity.vectorField?.currentMesh) {
      entity.vectorField.currentMesh.position = global.current.subtract(origin);
    }
  }

  // Place the grid relative to the camera's focus.
  const grid = state.sceneConfig.grid;
  if (grid && grid.spec.scale && grid.mesh) {
    // Data-space ground anchor derived inline each frame from current
    // scene state. Orbit follows the tracked entity's first cached Y;
    // third-person picks the first positioned entity. The user-supplied
    // `spec.verticalOffset` overrides both. Computing per-frame (rather
    // than snapshot-caching) lets the grid track the data as components
    // are reconfigured — a default-zero position spec replaced with
    // real topic data now repositions the grid as soon as the cache
    // reloads, with no toggle needed.
    const y = grid.spec.verticalOffset ?? deriveGroundY(state);
    if (y !== grid.verticalOffset) {
      // TODO: broadcast this computed value to the react state
      // to be able to show the default value in the panel settings.
      grid.verticalOffset = y;
    }
    const gridDataPosition = new babylon.Vector3(
      origin.x - (origin.x % grid.spec.scale),
      y,
      origin.z - (origin.z % grid.spec.scale),
    );
    grid.mesh.position = gridDataPosition.subtract(origin);
  }
}

/**
 * Resolves the grid's data-space Y anchor from current scene state.
 *
 * Orbit prefers the followed entity's first cached Y so the grid sits
 * under the tracked object. Third-person — and any orbit case where the
 * tracked entity hasn't loaded a cache yet — falls back to the first
 * positioned entity in iteration order. Returns `0` when no entity has
 * a populated cache (e.g. the data-load race window on initial
 * render); `applyRenderOrigin` will pick up the real anchor on the
 * next frame once a cache lands.
 */
function deriveGroundY(state: BabylonState): number {
  const camera = state.sceneConfig.camera;
  if (camera?.spec.type === schema.CAMERA_TYPE.orbit) {
    const trackedY = firstCachedY(state.entities[camera.spec.target.id]);
    if (trackedY !== undefined) {
      return trackedY;
    }
  }
  for (const entity of Object.values(state.entities)) {
    const firstY = firstCachedY(entity);
    if (firstY !== undefined) {
      return firstY;
    }
  }
  return 0;
}

/**
 * The entity's starting height in absolute data space.
 *
 * Prefers the resampled global trajectory, since a child's own cache holds
 * offsets in its parent's frame — a joint offset, not a ground height. Falls back
 * to the local cache while the global one is still being rebuilt, which is exact
 * for a root entity and the closest available answer for a child.
 */
function firstCachedY(entity: Entity | undefined): number | undefined {
  return (
    entity?.globalPosition?.cache.allPoints[0]?.y ??
    entity?.position?.cache.allPoints[0]?.y
  );
}

export const RENDER_INTERVAL_MS = 1000 / 60;
